import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PendingComment } from '@/types/comments';
import { cn } from '@/lib/utils';

interface PendingCommentItemProps {
  comment: PendingComment;
  onRetry: () => void;
  onRemove: () => void;
  className?: string;
}

export function PendingCommentItem({ 
  comment, 
  onRetry, 
  onRemove, 
  className 
}: PendingCommentItemProps) {
  return (
    <div className={cn("flex space-x-3 py-3", className)}>
      {/* User Avatar */}
      <div className="flex-shrink-0">
        <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center">
          <span className="text-sm font-bold text-white select-none">
            You
          </span>
        </div>
      </div>

      {/* Comment Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2 mb-1">
          <span className="font-semibold text-gray-300 text-sm">
            You
          </span>
          <span className="text-xs text-gray-500">
            {comment.status === 'pending' ? 'Posting...' : 'Failed to post'}
          </span>
        </div>
        
        <p className="text-gray-300 text-sm leading-relaxed break-words mb-2">
          {comment.content}
        </p>

        {/* Error State */}
        {comment.status === 'failed' && (
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-red-400" />
            <span className="text-xs text-red-400">
              {comment.error || 'Failed to post comment'}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={onRetry}
              className="h-6 px-2 text-xs text-red-500 hover:text-red-400"
            >
              <RefreshCw className="h-3 w-3 mr-1 text-red-500" />
              Retry
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onRemove}
              className="h-6 px-2 text-xs text-gray-400 hover:text-gray-300"
            >
              Remove
            </Button>
          </div>
        )}

        {/* Pending State */}
        {comment.status === 'pending' && (
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            <span className="text-xs text-gray-400">
              Posting comment...
            </span>
          </div>
        )}
      </div>
    </div>
  );
} 